import { useParams } from "@tanstack/react-router";

import type { Environment } from "@magicweave/api";

import { useEnvironments } from "../api/shell-hooks";
import { useCurrentEnvironment } from "./environment-context";

/**
 * What an environment is serving right now, said in one chip.
 *
 * This is the third fact the top strip carries, after the project and the
 * environment, and it is the one people get wrong: two environments of the same
 * project can legitimately be running different things, and a screen that names
 * only the environment leaves "which version of my game is this" unanswered.
 *
 * It lives apart from the shell components because the editor surfaces need the
 * same answer — a draft being edited is a draft against *some* environment, and
 * an editor header showing "Development · working draft" must not work it out
 * for a second time and reach a different conclusion.
 *
 * The three cases are all normal, and none of them is an error:
 *
 *  - an environment pinned to a release is serving that release;
 *  - an environment that follows the draft is serving whatever was last saved,
 *    which is what development environments are for;
 *  - an environment pinned to nothing has never been activated, which is what a
 *    new production looks like on the day it is created.
 *
 * The release number rather than the version, because the number is the
 * identity — monotonic, never reused — and the version is display text a studio
 * chooses. The version is still worth having, so it rides along in `detail`,
 * which the strip hangs off the chip rather than printing.
 */
/**
 * Which of the three cases this is, as a value rather than as the sentence
 * describing it.
 *
 * The shelf filters on it. Reading the filter off `label` instead would mean a
 * reworded chip silently emptied a filter — the copy and the classification
 * would be the same string doing two jobs, and only one of them would have a
 * test.
 */
export type ServingKind = "draft" | "dark" | "released";

export interface Serving {
  kind: ServingKind;
  /** Short enough for a chip in the top strip. */
  label: string;
  tone: "neutral" | "accent" | "warning";
  /** The longer sentence, for a tooltip or an editor's own header. */
  detail: string;
}

export function serving(environment: Environment | undefined): Serving | undefined {
  if (!environment) return undefined;

  if (environment.resolution_mode === "latest_draft") {
    return {
      // `warning`, not the accent. HOUSE-RULES §2: severity is never the
      // accent, draft → `warning`, and the Badge README makes the argument —
      // a shelf of twelve status badges in the accent reads as twelve calls to
      // action. The brand green means identity, not state.
      //
      // Nothing may read this tone to decide anything. `kind` above is the
      // fact; this is the drawing. `mechanics/copy.ts` once tested
      // `tone === "accent"` for "follows the draft", and this change would
      // have inverted it on every screen.
      kind: "draft",
      label: "Working draft",
      tone: "warning",
      detail: `${environment.name} serves whatever is in your draft, so anything you save is live here straight away.`,
    };
  }

  const release = environment.active_release;
  if (!release) {
    return {
      kind: "dark",
      label: "Nothing live yet",
      tone: "warning",
      detail: `${environment.name} has no release pointed at it yet, so games connecting to it have nothing to read.`,
    };
  }

  const version = release.version ? ` · version ${release.version}` : "";
  return {
    kind: "released",
    label: `Release #${release.release_number}`,
    tone: "neutral",
    detail: `${environment.name} is serving release ${release.release_number}${version}.`,
  };
}

/**
 * The environment the shell has chosen, in full.
 *
 * `useCurrentEnvironment` answers with an id, which is what a link needs and
 * not much else. A screen that wants to say something about the environment —
 * an editor header carrying "Development · working draft" beside its Draft
 * pill, a refusal that names which environment it applies to — wants the record
 * itself, and it should come from the list the shell has already fetched rather
 * than from a second request that can disagree with it.
 */
export function useCurrentEnvironmentDetail(): Environment | undefined {
  const { projectId } = useParams({ strict: false });
  const envId = useCurrentEnvironment();
  const environments = useEnvironments(projectId ?? "");
  if (!envId) return undefined;
  return (environments.data ?? []).find((e) => e.id === envId);
}

/** What the chosen environment is serving, or undefined before it is known. */
export function useServing(): Serving | undefined {
  return serving(useCurrentEnvironmentDetail());
}
